<script setup>
import { ref, getCurrentInstance } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { useLoading } from '@/composables/useLoading'

const { proxy } = getCurrentInstance()
const $t = proxy.$t
const { showLoading, hideLoading } = useLoading()

const passwordInput = ref(null)
const currentPasswordInput = ref(null)

const form = useForm({
    current_password: '',
    password: '',
    password_confirmation: '',
})

const updatePassword = () => {
    showLoading($t('Updating password...'));
    const routeName = proxy.route ? proxy.route('client.password.updatePassword') : '/password'
    form.put(routeName, {
        errorBag: 'updatePassword',
        preserveScroll: true,
        onSuccess: () => form.reset(),
        onError: () => {
            if (form.errors.password) {
                form.reset('password', 'password_confirmation')
                passwordInput.value?.$el?.focus()
            }

            if (form.errors.current_password) {
                form.reset('current_password')
                currentPasswordInput.value?.$el?.focus()
            }
        },
        onFinish: () => hideLoading(),
    })
}
</script>

<template>
    <form @submit.prevent="updatePassword" class="w-full">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
            <!-- Current Password -->
            <div class="field">
                <label for="current_password" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Current Password') }}
                </label>
                <Password
                    id="current_password"
                    ref="currentPasswordInput"
                    v-model="form.current_password"
                    :feedback="false"
                    toggleMask
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.current_password }"
                    autocomplete="current-password"
                    :placeholder="$t('Enter your current password')"
                />
                <small v-if="form.errors.current_password" class="p-error">
                    {{ form.errors.current_password }}
                </small>
            </div>

            <!-- New Password -->
            <div class="field">
                <label for="password" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('New Password') }}
                </label>
                <Password
                    id="password"
                    ref="passwordInput"
                    v-model="form.password"
                    :feedback="true"
                    toggleMask
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.password }"
                    autocomplete="new-password"
                    :placeholder="$t('Enter your new password')"
                    :weakLabel="$t('Weak')"
                    :mediumLabel="$t('Medium')"
                    :strongLabel="$t('Strong')"
                />
                <small v-if="form.errors.password" class="p-error">
                    {{ form.errors.password }}
                </small>
            </div>

            <!-- Confirm Password -->
            <div class="field">
                <label for="password_confirmation" class="block text-sm font-medium text-surface-700 mb-2">
                    {{ $t('Confirm Password') }}
                </label>
                <Password
                    id="password_confirmation"
                    v-model="form.password_confirmation"
                    :feedback="false"
                    toggleMask
                    class="w-full"
                    :class="{ 'p-invalid': form.errors.password_confirmation }"
                    autocomplete="new-password"
                    :placeholder="$t('Confirm your new password')"
                />
                <small v-if="form.errors.password_confirmation" class="p-error">
                    {{ form.errors.password_confirmation }}
                </small>
            </div>

            <!-- Actions -->
            <div class="flex flex-col justify-end">
                <Button
                    type="submit"
                    :label="form.processing ? $t('Updating...') : $t('Update Password')"
                    icon="pi pi-save"
                    :loading="form.processing"
                    :disabled="form.processing"
                    class="custom-button-height"
                />

                <div v-if="form.recentlySuccessful" class="flex items-center gap-2 text-green-600">
                    <i class="pi pi-check-circle"></i>
                    <span class="text-sm font-medium">{{ $t('Saved.') }}</span>
                </div>
            </div>
        </div>
    </form>
</template>

<style scoped>
.field {
    @apply flex flex-col;
}

.p-password {
    width: 100%;
}

.custom-button-height {
    height: 42px !important;
}

.p-error {
    @apply text-red-500 text-xs mt-1;
}

.p-invalid {
    @apply border-red-500;
}
</style>